import { AgGridReact } from "ag-grid-react";
import {
  AllCommunityModule,
  ColDef,
  GridOptions,
  GridReadyEvent,
  themeQuartz,
  RowClassParams,
  GetRowIdParams,
  GridApi,
  ModuleRegistry,
} from "ag-grid-community";
import { AllEnterpriseModule, LicenseManager } from "ag-grid-enterprise";
import { useCallback, useEffect, useMemo, useRef } from "react";

import { cn } from "../../utils";
import LoadingSpinner from "../LoadingSpinner";

import { BaseTableData, SimpleTableProps } from "./types";
import {
  createSimpleCellRenderer,
  getSimpleRowClasses,
  createSimpleContainerStyle,
} from "./utils";

import "./styles.css";

const DEFAULT_MIN_WIDTH = 100;
const PAGINATION_PAGE_SIZES = [10, 25, 50];
let agGridModulesRegistered = false;
let appliedAgGridLicenseKey: string | undefined;

function ensureAgGridModulesRegistered(): void {
  if (agGridModulesRegistered) {
    return;
  }

  ModuleRegistry.registerModules([AllCommunityModule, AllEnterpriseModule]);
  agGridModulesRegistered = true;
}

function applyAgGridLicenseKey(licenseKey?: string): void {
  if (!licenseKey || licenseKey === appliedAgGridLicenseKey) {
    return;
  }

  LicenseManager.setLicenseKey(licenseKey);
  appliedAgGridLicenseKey = licenseKey;
}

const getRowSelectionMode = (
  enableRowSelection: boolean | undefined,
  enableMultiRowSelection: boolean | undefined,
): "multiple" | "single" | undefined => {
  if (!enableRowSelection) {
    return undefined;
  }

  if (enableMultiRowSelection) {
    return "multiple";
  }

  return "single";
};

const INTERACTIVE_TARGET_SELECTOR = [
  "button",
  "a",
  "input",
  "select",
  "textarea",
  "[role='button']",
  "[role='menuitem']",
  "[role='option']",
  "[aria-haspopup]",
  "[data-radix-collection-item]",
].join(",");

export const SimpleTable = <T extends BaseTableData>({
  data,
  columns,
  rowModelType,
  datasource,
  isLoading,
  enableSorting = true,
  enableFiltering = false,
  enableResizing = true,
  enableRowSelection,
  enableMultiRowSelection,
  enablePagination,
  pageSize = 50,
  currentPage,
  onPaginationChanged,
  className,
  height,
  maxHeight,
  rowHeight = 48,
  headerHeight = 40,
  striped = false,
  hoverable = true,
  compact = false,
  getApiRef,
  onRowClick,
  getRowHref,
  onRowLinkClick,
  onRowDoubleClick,
  onSelectionChanged,
  onGridReady,
  emptyStateMessage = "No data available",
  emptyStateComponent,
  contextMenuItems,
  loadingComponent,
  shouldAutoSizeAllColumns,
  gridOptions: customGridOptions,
  theme = themeQuartz,
  getRowId,
  rowClass,
  suppressHeaderMenuButton = false,
  isHighlighted,
  licenseKey,
  ...tableProps
}: SimpleTableProps<T>) => {
  ensureAgGridModulesRegistered();
  applyAgGridLicenseKey(licenseKey);

  const gridRef = useRef<AgGridReact<T>>(null);
  const actualRowHeight = compact ? 36 : rowHeight;
  const actualHeaderHeight = compact ? 32 : headerHeight;

  useEffect(() => {
    if (getApiRef) {
      getApiRef(gridRef.current?.api as GridApi<T>);
    }
  }, [getApiRef, gridRef.current?.api]);

  useEffect(() => {
    if (isHighlighted && gridRef.current?.api) {
      gridRef.current.api.redrawRows();
    }
  }, [isHighlighted]);

  useEffect(() => {
    if (
      enablePagination &&
      gridRef.current?.api &&
      currentPage &&
      currentPage > 0
    ) {
      const agGridCurrentPage =
        gridRef.current.api.paginationGetCurrentPage() + 1;
      if (agGridCurrentPage !== currentPage) {
        gridRef.current.api.paginationGoToPage(currentPage - 1);
      }
    }
  }, [enablePagination, currentPage, gridRef.current?.api]);

  const containerStyle = useMemo(
    () =>
      createSimpleContainerStyle({
        hoverable,
        headerHeight: actualHeaderHeight,
        rowHeight: actualRowHeight,
        compact,
      }),
    [hoverable, actualHeaderHeight, actualRowHeight, compact],
  );

  const resolvedTheme = useMemo(() => {
    if (typeof theme === "string") {
      return theme;
    }
    return theme.withParams({
      fontSize: compact ? 13 : 14,
      headerFontSize: compact ? 12 : 13,
    });
  }, [theme, compact]);

  const { columnDefs, fitContentColIds } = useMemo(() => {
    const fitContentIds: string[] = [];
    const defs = columns.map((column) => {
      const {
        fitContent,
        suppressRowLink: _suppressRowLink,
        ...rest
      } = column;
      const columnWithId = column as { id?: number };
      let colId: string | undefined;
      if (columnWithId.id !== undefined) {
        colId = `col_${columnWithId.id}`;
      } else if (typeof column.field === "string") {
        colId = column.field;
      } else {
        colId = undefined;
      }
      if (fitContent && colId) {
        fitContentIds.push(colId);
      }
      const def: ColDef<T> = {
        ...rest,
        suppressHeaderMenuButton,
        headerName: column.header,
        flex: fitContent
          ? 0
          : (column.flex ??
            (column.width || column.minWidth || column.maxWidth
              ? undefined
              : 1)),
        width: column.width,
        minWidth: column.minWidth || DEFAULT_MIN_WIDTH,
        maxWidth: column.maxWidth,
        sortable:
          column.sortable !== undefined ? column.sortable : enableSorting,
        resizable:
          column.resizable !== undefined ? column.resizable : enableResizing,
        filter: column.filter !== undefined ? column.filter : enableFiltering,
        cellClass: cn("flex items-center", column.cellClass),
        headerClass: column.headerClass,
        suppressMovable: column.suppressMovable,
        cellRenderer:
          typeof column.cellRenderer === "string"
            ? column.cellRenderer
            : createSimpleCellRenderer(column, contextMenuItems, getRowHref, onRowLinkClick),
        autoHeight: false,
      };
      if (colId) {
        def.colId = colId;
      }
      return def;
    });
    return { columnDefs: defs, fitContentColIds: fitContentIds };
  }, [
    columns,
    enableSorting,
    enableResizing,
    enableFiltering,
    suppressHeaderMenuButton,
    contextMenuItems,
    getRowHref,
    onRowLinkClick,
  ]);

  const hasAutoSizedRef = useRef(false);

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: enableSorting,
      resizable: enableResizing,
      filter: enableFiltering,
    }),
    [enableSorting, enableResizing, enableFiltering],
  );

  const handleRowClick = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (event: any) => {
      const target = event.event.target as HTMLElement;
      const isCellClick = Boolean(target.closest(".ag-cell"));
      const isInteractiveElement = Boolean(target.closest(INTERACTIVE_TARGET_SELECTOR));

      if (onRowClick && event.data && isCellClick && !isInteractiveElement) {
        onRowClick(event.data, event.event as MouseEvent);
      }
    },
    [onRowClick],
  );

  const handleSelectionChanged = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (event: any) => {
      if (onSelectionChanged) {
        onSelectionChanged(event);
      }
    },
    [onSelectionChanged],
  );

  const handleGridReady = useCallback(
    (params: GridReadyEvent) => {
      if (shouldAutoSizeAllColumns && !hasAutoSizedRef.current) {
        params.api.sizeColumnsToFit();
        hasAutoSizedRef.current = true;
      }

      if (onGridReady) {
        onGridReady(params);
      }
    },
    [onGridReady, shouldAutoSizeAllColumns],
  );

  const gridOptions = useMemo<GridOptions<T>>(
    () => ({
      rowHeight: actualRowHeight,
      headerHeight: actualHeaderHeight,
      suppressCellFocus: true,
      enableCellTextSelection: true,
      suppressRowHoverHighlight: !hoverable,
      pagination: enablePagination,
      paginationPageSize: pageSize,
      paginationPageSizeSelector: enablePagination
        ? PAGINATION_PAGE_SIZES
        : false,
      domLayout: "normal",
      rowSelection: getRowSelectionMode(
        enableRowSelection,
        enableMultiRowSelection,
      ),
      suppressMoveWhenRowDragging: true,
      suppressScrollOnNewData: true,
      suppressAnimationFrame: false,
      suppressPropertyNamesCheck: true,
      autoSizeStrategy:
        fitContentColIds.length > 0
          ? { type: "fitCellContents" as const, colIds: fitContentColIds }
          : undefined,

      // eslint-disable-next-line react/no-unstable-nested-components
      loadingOverlayComponent: () => (
        <div className="flex h-full items-center justify-center bg-white/95 p-4">
          {loadingComponent ?? (
            <div className="text-sm text-gray-500">
              <LoadingSpinner size={4} />
            </div>
          )}
        </div>
      ),

      // eslint-disable-next-line react/no-unstable-nested-components
      noRowsOverlayComponent: () => (
        <div className="flex h-full items-center justify-center bg-white/95 p-8">
          {emptyStateComponent ?? (
            <div className="text-sm text-gray-500">{emptyStateMessage}</div>
          )}
        </div>
      ),
      ...customGridOptions,
    }),
    [
      actualRowHeight,
      actualHeaderHeight,
      hoverable,
      enablePagination,
      pageSize,
      enableRowSelection,
      enableMultiRowSelection,
      emptyStateMessage,
      emptyStateComponent,
      loadingComponent,
      customGridOptions,
      fitContentColIds,
    ],
  );

  const getRowClass = useCallback(
    (params: RowClassParams<T>) =>
      getSimpleRowClasses<T>({
        hoverable,
        onRowClick,
        onRowDoubleClick,
        striped,
        params,
        isHighlighted,
      }),
    [hoverable, onRowClick, onRowDoubleClick, striped, isHighlighted],
  );

  const getRowIdFunc = useCallback(
    (params: GetRowIdParams<T>) => {
      if (getRowId) {
        return getRowId(params);
      }
      return String(params.data?.id);
    },
    [getRowId],
  );

  return (
    <div
      className={cn(
        "pl-simpletable",
        "[&_.ag-cell]:p-0",
        "[&_.ag-group-contracted]:m-0 [&_.ag-group-contracted]:ml-2 [&_.ag-group-expanded]:m-0 [&_.ag-group-expanded]:ml-2",
        "[&_.ag-cell-wrapper]:h-full [&_.ag-cell-wrapper]:w-full",
        "[&_.ag-cell-value]:flex [&_.ag-cell-value]:h-full [&_.ag-cell-value]:w-full",
        "[&_.ag-group-value]:flex [&_.ag-group-value]:h-full [&_.ag-group-value]:w-full",
        onRowClick || onRowDoubleClick ? "[&_.ag-row]:cursor-pointer" : "",
        className,
      )}
      style={
        {
          width: "100%",
          height: height ?? "100%",
          maxHeight: maxHeight,
          ...containerStyle,
        } as React.CSSProperties
      }
    >
      <AgGridReact<T>
        ref={gridRef}
        rowData={rowModelType === "infinite" ? undefined : data}
        columnDefs={columnDefs}
        rowBuffer={25}
        defaultColDef={defaultColDef}
        theme={resolvedTheme}
        gridOptions={gridOptions}
        rowModelType={rowModelType}
        datasource={datasource}
        loading={isLoading}
        onRowClicked={handleRowClick}
        onSelectionChanged={handleSelectionChanged}
        onGridReady={handleGridReady}
        onPaginationChanged={onPaginationChanged}
        getRowClass={getRowClass}
        getRowId={getRowIdFunc}
        suppressContextMenu
        {...tableProps}
      />
    </div>
  );
};

export type {
  BaseTableData,
  SimpleTableColumn,
  SimpleTableProps,
} from "./types";
